import { useEffect } from 'react';
import { applySeo } from '../utils/seo';

/**
 * Applies per-route SEO tags (title, description, keywords, canonical, OG/Twitter
 * and JSON-LD) whenever the page mounts or its metadata changes.
 *
 * @param {object}   options
 * @param {string}   options.title       Page title, e.g. "FAQ". The brand is appended
 *                                       automatically unless the title already contains it.
 * @param {string}   options.description Meta description (155–160 characters works best).
 * @param {string}   options.keywords    Comma separated keywords for the page.
 * @param {string}   options.path        Route path used for the canonical URL (defaults to the browser URL).
 * @param {string}   options.image       Absolute image URL for social previews.
 * @param {string}   options.type        Open Graph type, defaults to "website".
 * @param {string}   options.robots      Robots directive, e.g. "noindex, follow".
 * @param {object[]} options.jsonLd      Extra structured data blocks (FAQPage, BreadcrumbList, …).
 * @param {string}   options.brand       Brand/site title from the API; falls back to the stored site title.
 * @param {string[]} options.sameAs      Social profile URLs used in the Organization schema.
 */
const useSEO = ({
  title,
  description,
  keywords,
  path,
  image,
  type,
  robots,
  jsonLd,
  brand,
  sameAs,
}) => {
  // Serialised so array/object literals from callers do not retrigger the effect on every render.
  const jsonLdKey = jsonLd && jsonLd.length ? JSON.stringify(jsonLd) : '';
  const sameAsKey = sameAs && sameAs.length ? JSON.stringify(sameAs) : '';

  useEffect(() => {
    return applySeo({
      title,
      description,
      keywords,
      path,
      image,
      type,
      robots,
      brand,
      jsonLd: jsonLdKey ? JSON.parse(jsonLdKey) : [],
      sameAs: sameAsKey ? JSON.parse(sameAsKey) : [],
    });
  }, [title, description, keywords, path, image, type, robots, brand, jsonLdKey, sameAsKey]);
};

export default useSEO;
